import { API_BASE } from '@/config';

export type SuggestionScopeType = 'selection' | 'full_document' | 'chapters';

export type SuggestionIntentResult = {
  wantsSuggestions: boolean;
  intent?: {
    scope: SuggestionScopeType;
    chapterTitles: string[];
    taskSummary: string;
    needsCharacterContext: boolean;
    needsLocationContext: boolean;
    requiresExhaustiveCoverage: boolean;
  };
  scopeLabel?: string;
  wordCount?: number;
  chunkCount?: number;
  estimatedCredits?: number;
  balance?: number;
  canAfford?: boolean;
};

export type ChatSuggestionStreamEvent =
  | { type: 'issue'; value: { text: string; type: string; feedback: string; suggestion?: string } }
  | { type: 'progress'; chunk: number; totalChunks: number; issuesSoFar: number }
  | { type: 'complete'; suggestionCount: number; creditsUsed: number; firstSuggestionIds: string[] }
  | { type: 'error'; value: string };

export type RunChatSuggestionsParams = {
  token: string;
  threadId: string;
  userMessageId: string;
  documentId: string;
  question: string;
  taskSummary: string;
  scope: SuggestionScopeType;
  scopeLabel?: string;
  chapterTitles?: string[];
  isSelectedText?: boolean;
  selectedText?: string;
  selectionFromPos?: number;
  selectionToPos?: number;
  needsCharacterContext?: boolean;
  needsLocationContext?: boolean;
  requiresExhaustiveCoverage?: boolean;
  onEvent?: (event: ChatSuggestionStreamEvent) => void;
};

export async function fetchSuggestionIntent(params: {
  token: string;
  question: string;
  documentId: string;
  threadId?: string;
  userMessageId?: string;
  isSelectedText?: boolean;
  selectedText?: string;
  chapterTitles?: string[];
}): Promise<SuggestionIntentResult> {
  const response = await fetch(`${API_BASE}/api/chat/suggestion-intent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    body: JSON.stringify({
      question: params.question,
      documentId: params.documentId,
      threadId: params.threadId,
      userMessageId: params.userMessageId,
      isSelectedText: params.isSelectedText,
      selectedText: params.selectedText,
      chapterTitles: params.chapterTitles,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch suggestion intent');
  }

  return response.json();
}

export async function runChatSuggestions(params: RunChatSuggestionsParams): Promise<{
  suggestionCount: number;
  creditsUsed: number;
  firstSuggestionIds: string[];
}> {
  const response = await fetch(`${API_BASE}/api/chat/suggestion-run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.token}`,
    },
    body: JSON.stringify({
      threadId: params.threadId,
      userMessageId: params.userMessageId,
      documentId: params.documentId,
      question: params.question,
      taskSummary: params.taskSummary,
      scope: params.scope,
      scopeLabel: params.scopeLabel,
      chapterTitles: params.chapterTitles,
      isSelectedText: params.isSelectedText,
      selectedText: params.selectedText,
      selectionFromPos: params.selectionFromPos,
      selectionToPos: params.selectionToPos,
      needsCharacterContext: params.needsCharacterContext,
      needsLocationContext: params.needsLocationContext,
      requiresExhaustiveCoverage: params.requiresExhaustiveCoverage,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || 'Failed to run chat suggestions');
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result = {
    suggestionCount: 0,
    creditsUsed: 0,
    firstSuggestionIds: [] as string[],
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }
      const event = JSON.parse(line) as ChatSuggestionStreamEvent;
      params.onEvent?.(event);
      if (event.type === 'complete') {
        result = {
          suggestionCount: event.suggestionCount,
          creditsUsed: event.creditsUsed,
          firstSuggestionIds: event.firstSuggestionIds,
        };
      }
      if (event.type === 'error') {
        throw new Error(event.value);
      }
    }
  }

  return result;
}

export function parseScopeLabel(scopeLabel: string): {
  scope: SuggestionScopeType;
  wordCount: number;
  chunkCount: number;
  chapterTitles: string[];
} | null {
  const parts = scopeLabel.split(':');
  if (parts[0] === 'selection' && parts.length >= 3) {
    return {
      scope: 'selection',
      wordCount: parseInt(parts[1], 10),
      chunkCount: parseInt(parts[2], 10),
      chapterTitles: [],
    };
  }
  if (parts[0] === 'full_document' && parts.length >= 3) {
    return {
      scope: 'full_document',
      wordCount: parseInt(parts[1], 10),
      chunkCount: parseInt(parts[2], 10),
      chapterTitles: [],
    };
  }
  if (parts[0] === 'chapters' && parts.length >= 4) {
    return {
      scope: 'chapters',
      chapterTitles: parts[1] ? parts[1].split('|').filter(Boolean) : [],
      wordCount: parseInt(parts[2], 10),
      chunkCount: parseInt(parts[3], 10),
    };
  }
  return null;
}

export function formatScopeLabel(scopeLabel: string, t: (key: string) => string): string {
  const parsed = parseScopeLabel(scopeLabel);
  if (!parsed) {
    return scopeLabel;
  }

  if (parsed.scope === 'selection') {
    return t('chat_suggestions_scope_selection')
      .replace('{wordCount}', String(parsed.wordCount))
      .replace('{chunkCount}', String(parsed.chunkCount));
  }

  if (parsed.scope === 'chapters') {
    const chapters = parsed.chapterTitles.join(', ');
    return t('chat_suggestions_scope_chapters')
      .replace('{chapters}', chapters)
      .replace('{wordCount}', String(parsed.wordCount))
      .replace('{chunkCount}', String(parsed.chunkCount));
  }

  return t('chat_suggestions_scope_full_document')
    .replace('{wordCount}', String(parsed.wordCount))
    .replace('{chunkCount}', String(parsed.chunkCount));
}
